// client/world — secret: biome layout, terrain, resource-node placement, mob spawn tables.
// Public: loadZone, nodesAt, spawnsFor.
// world/ may import three (per ESLint boundary rule) for terrain construction.

import {
  Mesh,
  PlaneGeometry,
  MeshToonMaterial,
  Group,
  CylinderGeometry,
  ConeGeometry,
  SphereGeometry,
  IcosahedronGeometry,
  BoxGeometry,
  Color,
  Vector3,
  type Object3D,
  RepeatWrapping,
  CanvasTexture,
  NearestFilter,
} from 'three';

export type ZoneId = 'greenshade';

export interface Zone {
  id: ZoneId;
  root: Object3D;
  spawnPos: Vector3;
  heightAt(x: number, z: number): number;
}

const FOREST_GREEN = new Color('#4a6a30');
const TRUNK_BROWN = new Color('#5a3a22');
const TRUNK_BIRCH = new Color('#d8d0b8');
const FOLIAGE_DARK = new Color('#2e4a22');
const FOLIAGE_MID = new Color('#4a7028');
const FOLIAGE_LIGHT = new Color('#7aa040');
const FOLIAGE_GOLD = new Color('#c8a040');
const ROCK_GRAY = new Color('#7a6f5a');
const MUSH_RED = new Color('#a83030');
const MUSH_STEM = new Color('#f0e8d0');
const FLOWER_PINK = new Color('#e890b0');
const FLOWER_WHITE = new Color('#f6f0e0');

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Smooth heightmap from low-frequency noise: rolling hills.
function makeHeightField(size: number, seed: number): Float32Array {
  const rng = mulberry32(seed);
  const grid = 8;
  const ctrl: number[] = [];
  for (let i = 0; i < (grid + 1) * (grid + 1); i++) ctrl.push(rng() * 4 - 1.5);

  const sample = (gx: number, gz: number): number => {
    const ix = Math.max(0, Math.min(grid, Math.floor(gx)));
    const iz = Math.max(0, Math.min(grid, Math.floor(gz)));
    return ctrl[iz * (grid + 1) + ix] ?? 0;
  };

  const hf = new Float32Array(size * size);
  for (let z = 0; z < size; z++) {
    for (let x = 0; x < size; x++) {
      const gx = (x / (size - 1)) * grid;
      const gz = (z / (size - 1)) * grid;
      const x0 = Math.floor(gx);
      const z0 = Math.floor(gz);
      const fx = gx - x0;
      const fz = gz - z0;
      const sx = fx * fx * (3 - 2 * fx);
      const sz = fz * fz * (3 - 2 * fz);
      const h00 = sample(x0, z0);
      const h10 = sample(x0 + 1, z0);
      const h01 = sample(x0, z0 + 1);
      const h11 = sample(x0 + 1, z0 + 1);
      const a = h00 + (h10 - h00) * sx;
      const b = h01 + (h11 - h01) * sx;
      hf[z * size + x] = a + (b - a) * sz;
    }
  }
  return hf;
}

function makeGrassTexture(): CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#3d5e2a';
  ctx.fillRect(0, 0, 64, 64);
  for (let i = 0; i < 60; i++) {
    ctx.fillStyle = i % 2 === 0 ? '#4a6a32' : '#345020';
    ctx.fillRect(Math.random() * 64, Math.random() * 64, 2, 2);
  }
  const tex = new CanvasTexture(canvas);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.magFilter = NearestFilter;
  tex.repeat.set(8, 8);
  return tex;
}

function makeFir(rng: () => number): Group {
  const g = new Group();
  const trunkH = 1.4 + rng() * 1.6;
  const trunk = new Mesh(
    new CylinderGeometry(0.16, 0.24, trunkH, 6),
    new MeshToonMaterial({ color: TRUNK_BROWN })
  );
  trunk.position.y = trunkH / 2;
  trunk.castShadow = true;
  g.add(trunk);

  const tiers = 3 + Math.floor(rng() * 2);
  for (let i = 0; i < tiers; i++) {
    const r = 1.5 - i * 0.3;
    const h = 1.5 - i * 0.18;
    const cone = new Mesh(
      new ConeGeometry(r, h, 7),
      new MeshToonMaterial({ color: i % 2 === 0 ? FOLIAGE_DARK : FOLIAGE_MID })
    );
    cone.position.y = trunkH + i * 0.65;
    cone.castShadow = true;
    g.add(cone);
  }
  return g;
}

function makeOak(rng: () => number): Group {
  const g = new Group();
  const trunkH = 1.3 + rng() * 0.8;
  const trunk = new Mesh(
    new CylinderGeometry(0.32, 0.42, trunkH, 7),
    new MeshToonMaterial({ color: TRUNK_BROWN })
  );
  trunk.position.y = trunkH / 2;
  trunk.castShadow = true;
  g.add(trunk);

  // Three foliage spheres clustered at top
  const palette = [FOLIAGE_MID, FOLIAGE_LIGHT, FOLIAGE_GOLD];
  for (let i = 0; i < 4; i++) {
    const r = 1.0 + rng() * 0.6;
    const sphere = new Mesh(
      new SphereGeometry(r, 10, 8),
      new MeshToonMaterial({ color: palette[i % palette.length] })
    );
    const ang = rng() * Math.PI * 2;
    const off = rng() * 0.7;
    sphere.position.set(Math.cos(ang) * off, trunkH + 0.5 + rng() * 0.4, Math.sin(ang) * off);
    sphere.castShadow = true;
    g.add(sphere);
  }
  return g;
}

function makeBirch(rng: () => number): Group {
  const g = new Group();
  const trunkH = 2.4 + rng() * 1.6;
  const trunk = new Mesh(
    new CylinderGeometry(0.12, 0.16, trunkH, 6),
    new MeshToonMaterial({ color: TRUNK_BIRCH })
  );
  trunk.position.y = trunkH / 2;
  trunk.castShadow = true;
  g.add(trunk);

  // Small puffy crown
  const crown = new Mesh(
    new SphereGeometry(0.9 + rng() * 0.3, 10, 8),
    new MeshToonMaterial({ color: FOLIAGE_LIGHT })
  );
  crown.position.y = trunkH + 0.5;
  crown.castShadow = true;
  g.add(crown);

  return g;
}

function makeStump(rng: () => number): Group {
  const g = new Group();
  const h = 0.35 + rng() * 0.2;
  const stump = new Mesh(
    new CylinderGeometry(0.5, 0.55, h, 8),
    new MeshToonMaterial({ color: TRUNK_BROWN })
  );
  stump.position.y = h / 2;
  stump.castShadow = true;
  stump.receiveShadow = true;
  g.add(stump);

  // Top wood ring slightly lighter
  const top = new Mesh(
    new CylinderGeometry(0.46, 0.46, 0.04, 8),
    new MeshToonMaterial({ color: '#8a6a44' })
  );
  top.position.y = h + 0.02;
  g.add(top);
  return g;
}

function makeRock(rng: () => number): Group {
  const g = new Group();
  const r = 0.25 + rng() * 0.5;
  const rock = new Mesh(
    new IcosahedronGeometry(r, 0),
    new MeshToonMaterial({ color: ROCK_GRAY })
  );
  rock.scale.y = 0.7 + rng() * 0.3;
  rock.rotation.set(rng() * Math.PI, rng() * Math.PI, rng() * Math.PI);
  rock.position.y = r * 0.6;
  rock.castShadow = true;
  rock.receiveShadow = true;
  g.add(rock);
  return g;
}

function makeFern(rng: () => number): Group {
  const g = new Group();
  for (let i = 0; i < 5; i++) {
    const cone = new Mesh(
      new ConeGeometry(0.12, 0.5 + rng() * 0.3, 5),
      new MeshToonMaterial({ color: FOLIAGE_LIGHT })
    );
    const ang = (i / 5) * Math.PI * 2;
    cone.position.set(Math.cos(ang) * 0.15, 0.25, Math.sin(ang) * 0.15);
    cone.rotation.set(0, ang, 0.4);
    g.add(cone);
  }
  return g;
}

function makeMushroom(rng: () => number): Group {
  const g = new Group();
  const stemH = 0.18 + rng() * 0.1;
  const stem = new Mesh(
    new CylinderGeometry(0.06, 0.07, stemH, 6),
    new MeshToonMaterial({ color: MUSH_STEM })
  );
  stem.position.y = stemH / 2;
  stem.castShadow = true;
  g.add(stem);

  const cap = new Mesh(
    new SphereGeometry(0.18, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2),
    new MeshToonMaterial({ color: MUSH_RED })
  );
  cap.position.y = stemH;
  cap.castShadow = true;
  g.add(cap);
  return g;
}

function makeFlower(rng: () => number): Group {
  const g = new Group();
  const color = rng() > 0.5 ? FLOWER_PINK : FLOWER_WHITE;
  // Stem
  const stem = new Mesh(
    new CylinderGeometry(0.02, 0.02, 0.3, 4),
    new MeshToonMaterial({ color: FOLIAGE_LIGHT })
  );
  stem.position.y = 0.15;
  g.add(stem);
  // Bloom: 4 small cones
  for (let i = 0; i < 4; i++) {
    const petal = new Mesh(
      new ConeGeometry(0.05, 0.08, 4),
      new MeshToonMaterial({ color })
    );
    const ang = (i / 4) * Math.PI * 2;
    petal.position.set(Math.cos(ang) * 0.05, 0.32, Math.sin(ang) * 0.05);
    petal.rotation.set(Math.PI / 2.3, 0, ang);
    g.add(petal);
  }
  return g;
}

const TREE_KINDS = [makeFir, makeFir, makeFir, makeOak, makeOak, makeBirch, makeStump] as const;
const PROP_KINDS = [makeRock, makeFern, makeFern, makeMushroom, makeFlower, makeFlower] as const;

const ZONE_SIZE_M = 100;
const HEIGHT_RES = 64;

export function loadZone(id: ZoneId): Zone {
  if (id !== 'greenshade') throw new Error(`client/world.loadZone: unknown zone ${id}`);

  const root = new Group();
  root.name = 'zone:greenshade';

  const heightField = makeHeightField(HEIGHT_RES, 0xc0ffee);
  const heightAt = (x: number, z: number): number => {
    const u = (x / ZONE_SIZE_M + 0.5) * (HEIGHT_RES - 1);
    const v = (z / ZONE_SIZE_M + 0.5) * (HEIGHT_RES - 1);
    if (u < 0 || u > HEIGHT_RES - 1 || v < 0 || v > HEIGHT_RES - 1) return 0;
    const ix = Math.floor(u);
    const iz = Math.floor(v);
    const fx = u - ix;
    const fz = v - iz;
    const idxClamp = (a: number): number => Math.max(0, Math.min(HEIGHT_RES - 1, a));
    const h00 = heightField[idxClamp(iz) * HEIGHT_RES + idxClamp(ix)] ?? 0;
    const h10 = heightField[idxClamp(iz) * HEIGHT_RES + idxClamp(ix + 1)] ?? 0;
    const h01 = heightField[idxClamp(iz + 1) * HEIGHT_RES + idxClamp(ix)] ?? 0;
    const h11 = heightField[idxClamp(iz + 1) * HEIGHT_RES + idxClamp(ix + 1)] ?? 0;
    const a = h00 + (h10 - h00) * fx;
    const b = h01 + (h11 - h01) * fx;
    return a + (b - a) * fz;
  };

  const geo = new PlaneGeometry(ZONE_SIZE_M, ZONE_SIZE_M, HEIGHT_RES - 1, HEIGHT_RES - 1);
  geo.rotateX(-Math.PI / 2);
  const positions = geo.attributes.position;
  if (!positions) throw new Error('PlaneGeometry missing position attribute');
  for (let i = 0; i < positions.count; i++) {
    const px = positions.getX(i);
    const pz = positions.getZ(i);
    positions.setY(i, heightAt(px, pz));
  }
  geo.computeVertexNormals();

  const groundMat = new MeshToonMaterial({ color: FOREST_GREEN, map: makeGrassTexture() });
  const ground = new Mesh(geo, groundMat);
  ground.receiveShadow = true;
  root.add(ground);

  const treeRng = mulberry32(0xf0e57);
  for (let i = 0; i < 90; i++) {
    const x = (treeRng() - 0.5) * (ZONE_SIZE_M - 6);
    const z = (treeRng() - 0.5) * (ZONE_SIZE_M - 6);
    if (Math.hypot(x, z) < 6) continue;
    const kindIdx = Math.floor(treeRng() * TREE_KINDS.length);
    const kind = TREE_KINDS[kindIdx] ?? makeFir;
    const t = kind(treeRng);
    const yawJitter = treeRng() * Math.PI * 2;
    t.rotation.y = yawJitter;
    t.position.set(x, heightAt(x, z), z);
    root.add(t);
  }

  // Ground props — scattered densely for forest-floor feel.
  const propRng = mulberry32(0xb05525);
  for (let i = 0; i < 200; i++) {
    const x = (propRng() - 0.5) * (ZONE_SIZE_M - 4);
    const z = (propRng() - 0.5) * (ZONE_SIZE_M - 4);
    if (Math.hypot(x, z) < 3) continue;
    const kindIdx = Math.floor(propRng() * PROP_KINDS.length);
    const kind = PROP_KINDS[kindIdx] ?? makeFern;
    const p = kind(propRng);
    p.rotation.y = propRng() * Math.PI * 2;
    p.position.set(x, heightAt(x, z), z);
    root.add(p);
  }

  return {
    id,
    root,
    spawnPos: new Vector3(0, heightAt(0, 0), 0),
    heightAt,
  };
}

export function nodesAt(_pos: Vector3, _radius: number): unknown[] {
  // Phase 3 will implement.
  return [];
}

export type EnemyKind = 'wolf';
export type EnemyState = 'idle' | 'chase' | 'attack' | 'flee' | 'dead';

export interface Enemy {
  id: string;
  kind: EnemyKind;
  hp: number;
  hpMax: number;
  pos: { x: number; y: number; z: number };
  yaw: number;
  state: EnemyState;
  homeX: number;
  homeZ: number;
  lastAttackMs: number;
  respawnAtMs: number;
}

const WOLF_HP_MAX = 60;
const WOLF_AGGRO_R = 8;
const WOLF_DEAGGRO_R = 16;
const WOLF_ATTACK_R = 1.9;
const WOLF_ATTACK_CD = 1500;
const WOLF_SPEED = 4.5;
const WOLF_FLEE_HP_PCT = 0.25;
const WOLF_RESPAWN_MS = 15_000;

export function spawnInitialEnemies(zone: Zone): Enemy[] {
  const out: Enemy[] = [];
  const rng = mulberry32(0xa11ce);
  const COUNT = 6;
  for (let i = 0; i < COUNT; i++) {
    let x = 0;
    let z = 0;
    for (let attempt = 0; attempt < 12; attempt++) {
      x = (rng() - 0.5) * 80;
      z = (rng() - 0.5) * 80;
      if (Math.hypot(x, z) > 12) break;
    }
    out.push({
      id: `wolf-${i}`,
      kind: 'wolf',
      hp: WOLF_HP_MAX,
      hpMax: WOLF_HP_MAX,
      pos: { x, y: zone.heightAt(x, z), z },
      yaw: rng() * Math.PI * 2,
      state: 'idle',
      homeX: x,
      homeZ: z,
      lastAttackMs: 0,
      respawnAtMs: 0,
    });
  }
  return out;
}

export function updateEnemies(
  enemies: Enemy[],
  player: { x: number; z: number },
  zone: Zone,
  dtMs: number,
  nowMs: number,
  onAttack: (e: Enemy) => void
): void {
  const dt = dtMs / 1000;
  for (const e of enemies) {
    if (e.state === 'dead') {
      if (nowMs >= e.respawnAtMs) {
        e.hp = e.hpMax;
        e.state = 'idle';
        e.pos.x = e.homeX;
        e.pos.z = e.homeZ;
        e.pos.y = zone.heightAt(e.homeX, e.homeZ);
      }
      continue;
    }

    const dx = player.x - e.pos.x;
    const dz = player.z - e.pos.z;
    const dist = Math.hypot(dx, dz);

    // State transitions
    const fleeing = e.hp / e.hpMax <= WOLF_FLEE_HP_PCT;
    let nextState: EnemyState = e.state;
    if (fleeing) nextState = 'flee';
    else if (dist <= WOLF_ATTACK_R) nextState = 'attack';
    else if (e.state === 'idle' && dist <= WOLF_AGGRO_R) nextState = 'chase';
    else if (e.state === 'chase' && dist > WOLF_DEAGGRO_R) nextState = 'idle';
    else if (e.state === 'attack' && dist > WOLF_ATTACK_R) nextState = 'chase';

    e.state = nextState;

    let mx = 0;
    let mz = 0;
    let speed = WOLF_SPEED;

    if (e.state === 'chase' && dist > 0.01) {
      mx = dx / dist;
      mz = dz / dist;
    } else if (e.state === 'flee' && dist > 0.01) {
      mx = -dx / dist;
      mz = -dz / dist;
      speed = WOLF_SPEED * 1.2;
    } else if (e.state === 'idle') {
      // Drift gently back toward home if displaced.
      const hx = e.homeX - e.pos.x;
      const hz = e.homeZ - e.pos.z;
      const hd = Math.hypot(hx, hz);
      if (hd > 1) {
        mx = hx / hd;
        mz = hz / hd;
        speed = WOLF_SPEED * 0.4;
      }
    } else if (e.state === 'attack') {
      // Attack on cooldown
      if (nowMs - e.lastAttackMs >= WOLF_ATTACK_CD) {
        e.lastAttackMs = nowMs;
        onAttack(e);
      }
      // Face player
      e.yaw = Math.atan2(dx, dz);
    }

    if (mx !== 0 || mz !== 0) {
      e.pos.x += mx * speed * dt;
      e.pos.z += mz * speed * dt;
      e.pos.y = zone.heightAt(e.pos.x, e.pos.z);
      e.yaw = Math.atan2(mx, mz);
    }
  }
}

export function spawnsFor(_zone: ZoneId): unknown[] {
  return [];
}

// ---- Resource nodes ----

export type ResourceKind = 'wood' | 'ore' | 'fiber';

export interface ResourceNode {
  id: string;
  kind: ResourceKind;
  tier: number;
  pos: { x: number; y: number; z: number };
  hp: number;
  hpMax: number;
  state: 'live' | 'depleted';
  respawnAtMs: number;
  rawDefId: string; // 'rawWood.t2' etc.
}

const NODE_HP: Record<number, number> = { 1: 60, 2: 100, 3: 160, 4: 240, 5: 360 };
const RESPAWN_MS = 30_000;

export function spawnInitialResourceNodes(zone: Zone): ResourceNode[] {
  const out: ResourceNode[] = [];
  const rng = mulberry32(0xc0de01);

  const kinds: ResourceKind[] = ['wood', 'ore', 'fiber'];
  // Density: per kind, drop ~10 nodes scattered
  let nextId = 0;
  for (const kind of kinds) {
    const count = 12;
    for (let i = 0; i < count; i++) {
      let x = 0;
      let z = 0;
      for (let attempt = 0; attempt < 12; attempt++) {
        x = (rng() - 0.5) * 80;
        z = (rng() - 0.5) * 80;
        if (Math.hypot(x, z) > 7) break;
      }
      const radius = Math.hypot(x, z);
      // Outer ring biased to T3, inner ring biased to T2
      const tier = radius > 30 && rng() > 0.45 ? 3 : 2;
      const id = `${kind}-${nextId++}`;
      out.push({
        id,
        kind,
        tier,
        pos: { x, y: zone.heightAt(x, z), z },
        hp: NODE_HP[tier] ?? 100,
        hpMax: NODE_HP[tier] ?? 100,
        state: 'live',
        respawnAtMs: 0,
        rawDefId: `raw${kind === 'wood' ? 'Wood' : kind === 'ore' ? 'Ore' : 'Fiber'}.t${tier}`,
      });
    }
  }
  return out;
}

export function updateResourceNodes(nodes: ResourceNode[], nowMs: number): void {
  for (const n of nodes) {
    if (n.state === 'depleted' && nowMs >= n.respawnAtMs) {
      n.state = 'live';
      n.hp = n.hpMax;
    }
  }
}

export interface GatherResult {
  damageDealt: number;
  yielded: ReadonlyArray<{ defId: string; count: number }>;
  depleted: boolean;
}

const GATHER_DAMAGE_PER_TICK = 25;

export function gatherTick(node: ResourceNode): GatherResult {
  if (node.state !== 'live') {
    return { damageDealt: 0, yielded: [], depleted: false };
  }
  const dmg = Math.min(node.hp, GATHER_DAMAGE_PER_TICK);
  node.hp -= dmg;
  // Each tick yields 1 raw unit, plus a chance for a bonus.
  const yielded: Array<{ defId: string; count: number }> = [{ defId: node.rawDefId, count: 1 }];
  if (Math.random() < 0.18) {
    yielded.push({ defId: node.rawDefId, count: 1 });
  }
  let depleted = false;
  if (node.hp <= 0) {
    node.state = 'depleted';
    node.respawnAtMs = performance.now() + RESPAWN_MS;
    depleted = true;
  }
  return { damageDealt: dmg, yielded, depleted };
}
